// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a component for displaying location addresses with consistent formatting.
 * It handles both cases where an address is present and where it's missing.
 */
import { Location } from '@/location/api/types';
import { Typography, TypographyProps } from '@mui/material';

/**
 * Props for the SiteAddressLabel component.
 * Extends Material-UI's Typography props to allow for customization of text appearance.
 */
interface Props extends TypographyProps {
  /**
   * A site object containing address information.
   * Only requires the address field from the Location type.
   */
  site: Pick<Location, 'address'>;
}

/**
 * A label component that displays the address of a site or a placeholder if
 * the address is empty.
 *
 * Features:
 * - Displays the full address if available
 * - Shows "No address" placeholder when address is null
 * - Preserves whitespace formatting with pre-wrap
 * - Uses secondary text color for visual hierarchy
 * - Inherits all Typography props for customization
 *
 * @example
 * ```tsx
 * // With address
 * <SiteAddressLabel site={{ address: "123 Main St, City, State" }} />
 *
 * // Without address
 * <SiteAddressLabel site={{ address: null }} />
 *
 * // With custom typography props
 * <SiteAddressLabel site={site} variant="body2" align="center" />
 * ```
 */
export function SiteAddressLabel(props: Props) {
  const { site, ...typographyProps } = props;

  // Use placeholder text if address is null
  const address =
    site.address === null ? 'No address' : site.address.formattedAddress;

  return (
    <Typography
      whiteSpace="pre-wrap"
      color="text.secondary"
      {...typographyProps}
    >
      {address}
    </Typography>
  );
}
